// 본문 미리보기 resolve에 실을 **위험도 평가 방식** 판정 — 순수 함수 층.
//
// 에디터의 실데이터 채널은 둘이다: 본문 캔버스는 `preview-by-context`, 작성폼 캔버스는
// `prefill-by-context`. 방식 변수를 바꾸면 prefill만 그 방식으로 재조회되고 preview는 아무것도
// 싣지 않아 서버가 계층 폴백(점검→현장→시스템)으로 해소했다. 그래서 같은 화면 안에서 범례·판정은
// 새 방식, 분기(행렬 크기)는 옛 방식으로 갈리는 조합이 남았다(2026-08-07 사용자 제보).
//
// ⚠️ **왜 "저작자가 캔버스에서 직접 고른 값"만 싣는가** — 순환 차단이 이 층의 존재 이유다.
// 작성폼 값 합성본(`writeCanvasValues` = defaults + prefill + edits)에서 방식을 뽑아 preview에
// 실으면 `preview → previewResult → prefill → writeCanvasPrefill → 방식 → preview`로 되돌아온다
// (prefill이 preview의 파생이기 때문이다). 사용자 편집 계층(`writeCanvasEdits`)은 preview에서
// 파생되지 않으므로 그 계층만 읽으면 **구조적으로** 순환이 없다.
//
// 고르기 전(edits에 없음)에는 아무것도 싣지 않는다 → 서버 계층 폴백 = 현행 동작 그대로(회귀 0).
// 변수의 리터럴 `default`를 억지로 싣지 않는 이유도 같다: 방식 변수는 `prefillFrom`을 가질 수 있어
// (SMS 양식이 그렇다) 초기값이 현장 설정에서 온다 — 리터럴을 실으면 초기 표시가 지금보다 나빠진다.
//
// 판정 규칙 자체는 발명하지 않는다. 트리거 키 집합은 `collectCatalogParamVariableKeys`, 값 해소는
// `resolveFindingRiskMethodValue`(둘 다 rc `catalogParamTrigger` — 서버 해소의 거울)를 그대로 쓴다.

import {
  collectCatalogParamVariableKeys,
  resolveFindingRiskMethodValue,
} from '@safetyhub/report-composer';
import type { ReportTemplate } from '@safetyhub/shared';

/**
 * 저작자가 캔버스에서 **직접 고른** 위험도 평가 방식 — 미리보기 컨텍스트의 `findingRiskMethod`.
 *
 * @param template 편집 중 템플릿(미저장 포함) — 트리거 축·지정 소유 규칙의 근거.
 * @param edits    작성 탭 캔버스의 사용자 편집 오버레이(`writeCanvasEdits`). **이 계층만** 읽는다.
 * @returns 실어 보낼 방식 값. 저작자 선택이 없으면 `undefined`(= 서버 계층 폴백 유지).
 *
 * ```
 * ① 재resolve 트리거 축이 없는 양식(지정 없음 / 지정이 literal 하나뿐) → undefined
 *    (literal 고정 지정은 서버가 진입점에서 이미 정본으로 덮는다 — 클라가 실을 이유가 없다.)
 * ② 트리거 키 중 **편집된 것이 하나도 없으면** → undefined (아직 안 골랐다 = 현행 동작)
 * ③ 편집이 있으면 → rc 규칙(지정이 축을 소유)으로 edits에서 해소한 값
 *    지정 키가 아닌 다른 트리거 변수만 편집된 경우엔 해소가 비어 undefined로 떨어진다(옳다 —
 *    방식 축이 움직이지 않았으므로 폴백 유지).
 * ```
 */
export function resolveAuthoredFindingRiskMethod(
  template: ReportTemplate | null | undefined,
  edits: Record<string, unknown>,
): string | undefined {
  const triggerKeys = collectCatalogParamVariableKeys(template);
  if (triggerKeys.size === 0) return undefined;
  let authored = false;
  for (const key of triggerKeys) {
    if (Object.prototype.hasOwnProperty.call(edits, key)) {
      authored = true;
      break;
    }
  }
  if (!authored) return undefined;
  return resolveFindingRiskMethodValue(template, edits);
}
